// ─── VENDORED — DO NOT EDIT ─────────────────────────────────────────────────
// CONTRACT:I4-POLICY-DECISION.1.0 — vendored decision/marking substrate (see README.md)
// Source: willackerly/TDFLite tag v0.10.0 (commit 2a1ec7cb639a7a77b8ec9e14df4cb0b139cb43aa)
// Path:   js/packages/classification-schema/src/descope.ts
// Sync:   scripts/sync-classification-schema.sh (re-vendors on a pin bump)
// Drift:  scripts/check-classification-schema-drift.sh (this header + the
//         "@tdflite/contracts" specifier rewrite + the de-triggered upstream
//         contract line are NORMALIZED; any OTHER divergence FAILS the gate).
// ─── END VENDORED HEADER ─────────────────────────────────────────────────────
// upstream-contract F1-CLASSIFICATION-SCHEMA.0.1 (TDFLite; see vendored header)
/** "descope / view-as" — a voluntary, decision-time STEP-DOWN that STRENGTHENS the
 *  shipped need-to-know read-down.
 *
 *  An operator may narrow their OWN held clearance DOWN — view-as-lower-clearance,
 *  view-as-coalition-partner — so the fabric can PROVE a step-down can only ever HIDE more,
 *  never reveal more. It is DECISION-TIME ONLY: no re-mint, no privilege change, reversible
 *  and composable for free.
 *
 *  This adds ZERO decision machinery. `descopeDecide` is PURE COMPOSITION over the shipped,
 *  UNMODIFIED read-down evaluator (evaluateAbac):
 *
 *      descopeDecide(boundFqns, bundle, held, to)
 *        === evaluateAbac(boundFqns, bundle, narrowHeld(bundle, held, to))
 *
 *  Read-down, grouping, FQN parsing, and fail-closed logic stay EXCLUSIVELY in the evaluator
 *  — the single source of truth. `narrowHeld` only steps the operator's held claims DOWN per
 *  axis before the evaluator runs. When `to` is {}, narrowHeld is the identity, so
 *  descopeDecide is a strict generalization of the shipped decision.
 *
 *  Byte-parity with the Go twin (pkg/pdp/classification/descope.go) by construction: both
 *  engines read the SAME staged vectors + the SAME seed bundle and drive the SAME evaluator
 *  through the SAME narrow step. Identical inputs => identical verdict. */

import { CLASS_LEVELS, classRank } from "./lattice";
import { evaluateAbac } from "./evaluate";
import type { AttributeDef, Decision, PolicyBundle, SubjectClaims } from "./evaluate";

/** A voluntary step-down target, keyed by axis (bundle attribute name). Per axis, the
 *  values the operator CHOOSES to keep in view. DOWN-ONLY by construction:
 *    - an axis OMITTED here passes through unchanged (silence narrows nothing);
 *    - an axis present as an EXPLICIT EMPTY [] narrows that axis to nothing;
 *    - a value named here that the operator does not already hold contributes NOTHING —
 *      the fabric never grants it. */
export type DescopeSpec = Record<string, string[]>;

const lc = (s: string): string => String(s).toLowerCase();

/** Project a claim/spec value to a normalized, case-folded string[] using the SAME rule as
 *  the evaluator's userValuesFor: an array folds element-wise; a non-empty scalar becomes a
 *  singleton; absent or '' becomes []. Keeping this projection identical to the evaluator is
 *  what makes both engines classify the same values the same way. */
function projectValues(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value.map(lc);
  if (value != null && value !== "") return [lc(String(value))];
  return [];
}

/** Look up an axis's rule the SAME way the evaluator does (case-insensitive by name), so the
 *  BUNDLE — never a hardcoded axis name — decides whether an axis is the classification
 *  ceiling (hierarchy) or a set. An axis unknown to the bundle is treated as a SET axis; the
 *  evaluator, the single fail-closed authority, denies it downstream. */
function attrByName(bundle: PolicyBundle, name: string): AttributeDef | undefined {
  return bundle.attributes.find((a) => lc(a.name) === lc(name));
}

/** Clamp the classification CEILING down. The narrowed rank is min(rank(held), rank(target))
 *  on CLASS_ORDER (0=UNCLASSIFIED .. 4=TOP_SECRET, higher = more sensitive), so a target
 *  ABOVE the held level is a no-op CLAMP — never a widen. Fail-closed: no held level, an
 *  EXPLICIT EMPTY target, or an unrankable target all narrow to [] (deny any classified
 *  object).
 *
 *  HELD is recognized with the EVALUATOR'S OWN exact matcher — def.values membership,
 *  lowercase, NO trim (definedIdx in evaluate.ts) — never classRank's more lenient
 *  .trim()ing normalize. This is load-bearing for the down-only invariant: the read-down
 *  evaluator matches held via that exact matcher, so a held clearance differing from a
 *  canonical level only by surrounding whitespace (e.g. " secret") is UNRECOGNIZED there and
 *  fail-closed denied. Ranking it with the looser classRank would re-emit a clean canonical
 *  "SECRET" and out-permit the evaluator's own read of held — a down-only WIDEN. So an
 *  unrecognized (or off-ladder) held value passes through untouched for the evaluator — the
 *  sole authority — to deny. Output is the canonical CLASS_LEVELS spelling; the evaluator
 *  re-lowercases. */
function narrowHierarchy(def: AttributeDef, heldVals: string[], descopeVals: string[]): string[] {
  if (heldVals.length === 0) return []; // nothing to narrow — stays fail-closed
  if (descopeVals.length === 0) return []; // EXPLICIT EMPTY target => narrow to nothing
  const target = classRank(descopeVals[0]!);
  if (target === undefined) return []; // unknown target level => fail-closed, never widen
  // Recognize held with the EVALUATOR'S exact matcher (def.values membership, lowercase, no
  // trim), so the clamp can never out-permit the evaluator's read of held (whitespace guard).
  const definedIdx = def.values.findIndex((x) => lc(x) === lc(heldVals[0]!));
  if (definedIdx < 0) return heldVals; // unrecognized held => let the evaluator deny it
  const held = classRank(heldVals[0]!);
  if (held === undefined) return heldVals; // off-ladder held => let the evaluator deny it
  return [CLASS_LEVELS[Math.min(held, target)]!];
}

/** Narrow the operator's OWN held claims DOWN per the step-down target. Returns a NEW claims
 *  map; NEVER mutates `held`. The bundle is read ONLY to classify each axis's rule.
 *
 *  Per axis:
 *    (1) OMITTED from `to`      => pass through unchanged (down-only default, normalized to string[]).
 *    (2) PRESENT in `to`:
 *        (2a) hierarchy rule    => classification CEILING clamp (see narrowHierarchy).
 *        (2b) set rule (allOf | anyOf | axis unknown to bundle)
 *                               => held ∩ target, preserving HELD order, case-insensitive.
 *                                  DOWN-ONLY: a target value not already held drops out; an
 *                                  EXPLICIT EMPTY [] narrows the axis to nothing.
 *
 *  Reversible / composable for free: descopeTo ⊇ held on an axis is the identity, and
 *  narrowHeld(narrowHeld(h, a), b) == narrowHeld(h, per-axis-intersection(a, b)). */
export function narrowHeld(bundle: PolicyBundle, held: SubjectClaims, to: DescopeSpec): SubjectClaims {
  const narrowed: SubjectClaims = {};

  // Deterministic, engine-identical axis order: held keys first (as encountered), then any
  // descopeTo-only keys (as encountered). The verdict is order-independent — the evaluator
  // re-groups by bound FQN — so this only fixes a stable shape.
  const heldKeys = Object.keys(held);
  const heldKeySet = new Set(heldKeys);
  const axes = [...heldKeys, ...Object.keys(to).filter((k) => !heldKeySet.has(k))];

  for (const axis of axes) {
    // (1) Axis OMITTED from the step-down target => pass through unchanged.
    if (!Object.prototype.hasOwnProperty.call(to, axis)) {
      narrowed[axis] = projectValues(held[axis]);
      continue;
    }

    // (2) Axis PRESENT in the step-down target. The bundle's rule decides ceiling vs set.
    const def = attrByName(bundle, axis);
    const heldVals = projectValues(held[axis]);
    const descopeVals = projectValues(to[axis]);

    if (def && def.rule === "hierarchy") {
      // (2a) Classification ceiling clamp — lower the ceiling only, never widen.
      narrowed[axis] = narrowHierarchy(def, heldVals, descopeVals);
    } else {
      // (2b) SET axis: keep only what the operator ALREADY holds AND chooses to keep.
      narrowed[axis] = heldVals.filter((v) => descopeVals.includes(v));
    }
  }

  return narrowed;
}

/** Decide whether an operator, having VOLUNTARILY stepped their view DOWN, may still SEE an
 *  object. PURE COMPOSITION — the whole body is evaluateAbac over the narrowed held claims.
 *  It MUST NOT reimplement read-down, grouping, FQN parsing, or fail-closed logic; those stay
 *  exclusively in the unmodified evaluator. `Decision.permit` is the hard, engine-agreed
 *  contract; `reason` / `verdicts` are advisory. */
export function descopeDecide(
  boundFqns: string[],
  bundle: PolicyBundle,
  held: SubjectClaims,
  to: DescopeSpec,
): Decision {
  return evaluateAbac(boundFqns, bundle, narrowHeld(bundle, held, to));
}
